/**
 * Step-up chooser bridge (STEPUP_FALLBACK_DESIGN.md §7). A single `<StepUpChooser>` is mounted at
 * the app shell; callers invoke `runStepUp(purpose, factors)` to open it and await a grant. The
 * promise resolves when a factor yields a grant, or rejects with {@link StepUpCancelled} if the
 * client dismisses the sheet — so a broken Face ID never dead-ends behind the browser's USB picker.
 */
import { reactive, readonly } from 'vue';
import type { PendingStepUpFactor, StepUpFactor, StepUpPurpose } from '@/api/stepup';

export class StepUpCancelled extends Error {
  constructor() {
    super('step-up cancelled');
    this.name = 'StepUpCancelled';
  }
}

interface StepUpState {
  open: boolean;
  purpose: StepUpPurpose;
  factors: StepUpFactor[];
  /** Factors not usable for this purpose yet (the key export: a PIN / passkey enrolled < 3 days ago). */
  pending: PendingStepUpFactor[];
}

const state = reactive<StepUpState>({ open: false, purpose: 'REVEAL', factors: [], pending: [] });

let resolveGrant: ((grant: string) => void) | null = null;
let rejectGrant: ((err: Error) => void) | null = null;

function reset() {
  state.open = false;
  state.factors = [];
  state.pending = [];
  resolveGrant = null;
  rejectGrant = null;
}

/** Open the chooser for {@code purpose}, offering {@code factors} and noting {@code pending} (from the 403 body). */
function runStepUp(purpose: StepUpPurpose, factors: StepUpFactor[],
                   pending: PendingStepUpFactor[] = []): Promise<string> {
  // A step-up already in flight is superseded — reject the old waiter so it doesn't leak.
  rejectGrant?.(new StepUpCancelled());
  return new Promise<string>((resolve, reject) => {
    state.purpose = purpose;
    state.factors = factors;
    state.pending = pending;
    state.open = true;
    resolveGrant = resolve;
    rejectGrant = reject;
  });
}

/** Called by the chooser when a factor produced a grant. */
function complete(grant: string) {
  resolveGrant?.(grant);
  reset();
}

/** Called by the chooser when the client dismisses it. */
function cancel() {
  rejectGrant?.(new StepUpCancelled());
  reset();
}

export function useStepUp() {
  return { state: readonly(state), runStepUp, complete, cancel };
}
